import {Component, effect, inject, input, signal} from '@angular/core';
import {timeAgo} from 'src/app/domain/util/timestamps/timestamps';
import {statusColor} from 'src/app/domain/assets/status/status';
import {Animal} from 'src/app/infrastructure/moovement-api/models/asset';
import {AggregatedAssetStateStore} from 'src/app/domain/assets/aggregated-state/aggregated-asset-state.store';
import {AnimalsStore} from 'src/app/domain/assets/animals/animals.store';
import {DeviceStore} from 'src/app/domain/devices/device.store';
import {DeviceType} from 'src/app/domain/devices/device';
import {AggregatedDeviceStateStore} from 'src/app/domain/devices/aggregated-state/aggregated-device-state.store';
import {isNfcTag} from "src/app/domain/devices/tag/tag";
import {NfcLocationMetadataStore} from "src/app/domain/samples/nfc-location/nfc-location.store";
import {Samples} from "src/app/infrastructure/moovement-api/models/samples";
import NfcLocationSampleMetadata = Samples.NfcLocationSampleMetadata;

@Component({
  selector: 'app-animal-status-bar',
  imports: [],
  templateUrl: './animal-status-bar.component.html',
  styleUrl: './animal-status-bar.component.css'
})
export class AnimalStatusBarComponent {

  animal = input<Animal>();
  observingDeviceId = input<string>();

  deviceId = signal<string | undefined>(undefined);

  showGpsData = signal(false);

  lastLocationAt = signal<Date | undefined>(undefined);
  lastSeenBySatelliteMessageColorClass = signal('color-danger');
  lastSeenBySatelliteMessage = signal('?');

  lastSeenByGateway = signal<Date | undefined>(undefined);
  lastSeenByLoRaGatewayColorClass = signal('color-danger');
  lastSeenByLoRaGatewayMessage = signal('?');

  lastSeenByBLEScanner = signal<Date | undefined>(undefined);
  lastSeenByBLEScannerMessage = signal('?');
  lastSeenByBLEGatewayMessageColorClass = signal('color-danger');
  nfcScanMoments = signal<NfcLocationSampleMetadata[]>([]);
  private readonly aggregatedAssetStateStore = inject(AggregatedAssetStateStore);
  private readonly aggregatedDeviceStateStore = inject(AggregatedDeviceStateStore);
  private readonly nfcLocationMetadataStore = inject(NfcLocationMetadataStore);
  private readonly animalsStore = inject(AnimalsStore);
  private readonly deviceStore = inject(DeviceStore);

  constructor() {
    effect(() => {
      const assetId = this.animal()?.id;
      if (!assetId) return;

      const aggregatedAnimalState = this.aggregatedAssetStateStore.entityMap()[assetId];

      this.lastLocationAt.set(aggregatedAnimalState?.lastGPSFixAt);
      this.lastSeenByGateway.set(aggregatedAnimalState?.lastSeenByGatewayAt);

      const observingDeviceId = this.observingDeviceId();
      if (!observingDeviceId) {
        this.lastSeenByBLEScanner.set(aggregatedAnimalState?.lastSeenByBLEScannerAt);
      } else {
          const deviceId = this.deviceId();
          const observingDeviceState = this.aggregatedDeviceStateStore.entityMap()[observingDeviceId];
          if (!deviceId || !observingDeviceState) return;

          this.lastSeenByBLEScanner.set(observingDeviceState.lastObservationPerDevice.get(deviceId));
      }
    });

    effect(() => {
      const assetId = this.animal()?.id;
      if (!assetId) return;

      const deviceId = this.animalsStore.animalIdToDeviceId().get(assetId);
      this.deviceId.set(deviceId);
    });

    effect(() => {
      const deviceId = this.deviceId();
      if (!deviceId) return;

      const deviceType = this.deviceStore.deviceIdToType().get(deviceId);
      if (!deviceType) return;

      if (deviceType == DeviceType.GPS_PROXIMITY_ACCELEROMETER_TAG) {
        this.showGpsData.set(true);
      } else if (deviceType == DeviceType.GPS_TAG) {
        this.showGpsData.set(true);
      } else if (deviceType === DeviceType.PROXIMITY_TAG) {
        this.showGpsData.set(true);
      }
    });

    effect(() => {
      this.setLastSeenBySatellite(this.lastLocationAt());
    });

    effect(() => {
      this.setLastSeenByGateway(this.lastSeenByGateway());
    });

    effect(() => {
      this.setLastSeenByBLEScanner(this.lastSeenByBLEScanner());
    });

    effect(() => {
      const assetId = this.animal()?.id;
      if (!assetId) return;

      if (!this.isNfcTag) return;

      this.nfcScanMoments.set(this.nfcLocationMetadataStore.metadata().sort((a, b) => b.scannedAt.getTime() - a.scannedAt.getTime()));
    });
  }

  private setLastSeenByBLEScanner(lastSeenByBLEScanner?: Date) {
    this.lastSeenByBLEScannerMessage.set(timeAgo(lastSeenByBLEScanner, ''));
    this.lastSeenByBLEGatewayMessageColorClass.set(statusColor(lastSeenByBLEScanner));
  }

  private setLastSeenBySatellite(lastSeenBySatellite?: Date) {
    this.lastSeenBySatelliteMessage.set(timeAgo(lastSeenBySatellite, ''));
    this.lastSeenBySatelliteMessageColorClass.set(statusColor(lastSeenBySatellite));
  }

  private setLastSeenByGateway(lastSeenByGateway?: Date) {
    this.lastSeenByLoRaGatewayMessage.set(timeAgo(lastSeenByGateway, ''));
    this.lastSeenByLoRaGatewayColorClass.set(statusColor(lastSeenByGateway));
  }



protected readonly isNfcTag = isNfcTag;
  protected readonly timeAgo = timeAgo;
}
